import { useEffect, useRef, useState } from 'react'
import { Box, Container, Flex, Text, useColorMode } from '@chakra-ui/react'

export function DemoSection() {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'

  // Demo 1 state (code editor)
  const [step, setStep] = useState(0)
  const [passcodeText, setPasscodeText] = useState('')
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // Demo 2 state (standalone chat)
  const [step2, setStep2] = useState(0)
  const timeouts2Ref = useRef<ReturnType<typeof setTimeout>[]>([])

  // Demo 3 state (editor dashboard)
  const [step3, setStep3] = useState(0)
  const timeouts3Ref = useRef<ReturnType<typeof setTimeout>[]>([])

  const clearTimeouts = () => {
    timeoutsRef.current.forEach(t => clearTimeout(t))
    timeoutsRef.current = []
  }

  const clearTimeouts2 = () => {
    timeouts2Ref.current.forEach(t => clearTimeout(t))
    timeouts2Ref.current = []
  }

  const clearTimeouts3 = () => {
    timeouts3Ref.current.forEach(t => clearTimeout(t))
    timeouts3Ref.current = []
  }

  const typePasscode = (text: string, index = 0) => {
    if (index <= text.length) {
      setPasscodeText(text.substring(0, index))
      if (index < text.length) {
        const t = setTimeout(() => typePasscode(text, index + 1), 100)
        timeoutsRef.current.push(t)
      }
    }
  }

  const playDemo = () => {
    clearTimeouts()
    setStep(0)
    setPasscodeText('')

    // Step 1: Show user message
    timeoutsRef.current.push(setTimeout(() => setStep(1), 500))
    // Step 2: Show assistant thinking
    timeoutsRef.current.push(setTimeout(() => setStep(2), 1500))
    // Step 3: Tool completes
    timeoutsRef.current.push(setTimeout(() => setStep(3), 3000))
    // Step 4: Show link
    timeoutsRef.current.push(setTimeout(() => setStep(4), 3500))
    // Step 5: Switch to browser
    timeoutsRef.current.push(setTimeout(() => setStep(5), 5000))
    // Step 6: Show passcode prompt
    timeoutsRef.current.push(setTimeout(() => setStep(6), 5500))
    // Step 7: Type passcode
    timeoutsRef.current.push(setTimeout(() => typePasscode('team2024'), 6500))
    // Step 8: Show content
    timeoutsRef.current.push(setTimeout(() => setStep(8), 8000))
    // Step 9: Restart
    timeoutsRef.current.push(setTimeout(() => playDemo(), 12000))
  }

  const playDemo2 = () => {
    clearTimeouts2()
    setStep2(0)

    // Step 1: Show user message
    timeouts2Ref.current.push(setTimeout(() => setStep2(1), 500))
    // Step 2: Show assistant text
    timeouts2Ref.current.push(setTimeout(() => setStep2(2), 1500))
    // Step 3: Show tool call header
    timeouts2Ref.current.push(setTimeout(() => setStep2(3), 2500))
    // Step 4: Show tool params
    timeouts2Ref.current.push(setTimeout(() => setStep2(4), 3500))
    // Step 5: Tool completes
    timeouts2Ref.current.push(setTimeout(() => setStep2(5), 4500))
    // Step 6: Show result text
    timeouts2Ref.current.push(setTimeout(() => setStep2(6), 5000))
    // Step 7: Show link
    timeouts2Ref.current.push(setTimeout(() => setStep2(7), 5500))
    // Step 8: Switch to browser
    timeouts2Ref.current.push(setTimeout(() => setStep2(8), 7000))
    // Step 9: Show rendered content
    timeouts2Ref.current.push(setTimeout(() => setStep2(9), 7500))
    // Step 10: Restart
    timeouts2Ref.current.push(setTimeout(() => playDemo2(), 12000))
  }

  const playDemo3 = () => {
    clearTimeouts3()
    setStep3(0)

    // Step 1: Show sidebar with shares list
    timeouts3Ref.current.push(setTimeout(() => setStep3(1), 500))
    // Step 2: Highlight first item
    timeouts3Ref.current.push(setTimeout(() => setStep3(2), 1500))
    // Step 3: Click to view
    timeouts3Ref.current.push(setTimeout(() => setStep3(3), 2500))
    // Step 4: Show content preview
    timeouts3Ref.current.push(setTimeout(() => setStep3(4), 3000))
    // Step 5: Highlight second item
    timeouts3Ref.current.push(setTimeout(() => setStep3(5), 5000))
    // Step 6: Click to view second
    timeouts3Ref.current.push(setTimeout(() => setStep3(6), 6000))
    // Step 7: Show second content
    timeouts3Ref.current.push(setTimeout(() => setStep3(7), 6500))
    // Step 8: Restart
    timeouts3Ref.current.push(setTimeout(() => playDemo3(), 10000))
  }

  // Start all demos on mount
  useEffect(() => {
    const t1 = setTimeout(playDemo, 500)
    const t2 = setTimeout(playDemo2, 500)
    const t3 = setTimeout(playDemo3, 500)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
      clearTimeouts()
      clearTimeouts2()
      clearTimeouts3()
    }
  }, [])

  // Colors
  const containerBg = isDark ? '#1c1917' : '#ffffff'
  const containerBorder = isDark ? '#292524' : '#e7e5e4'
  const chromeBg = isDark ? '#292524' : '#f5f5f4'
  const panelBg = isDark ? '#0c0a09' : '#fafaf9'
  const textPrimary = isDark ? '#fafaf9' : '#1c1917'
  const textSecondary = isDark ? '#a8a29e' : '#78716c'
  const textMuted = isDark ? '#78716c' : '#a8a29e'
  const codeBg = isDark ? '#292524' : '#f5f5f4'
  const inputBg = isDark ? '#0c0a09' : '#ffffff'
  const inputBorder = isDark ? '#292524' : '#e7e5e4'

  // Section background colors (alternating)
  const sectionBg1 = isDark ? '#0c0a09' : '#fafaf9'
  const sectionBg2 = isDark ? 'transparent' : 'white'

  return (
    <>
    {/* First demo section - Code Editor */}
    <Box py={{ base: 16, md: 24 }} bg={sectionBg1}>
      <Container maxW="1000px" px={4}>
        {/* Section header */}
        <Box textAlign="center" mb={{ base: 8, md: 12 }}>
          <Text
            fontSize="sm"
            fontWeight={600}
            color="#10b981"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={3}
          >
            Features
          </Text>
          <Text
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight={700}
            color={textPrimary}
            mb={3}
          >
            Use in your favorite code editor
          </Text>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color={textSecondary}
            maxW="600px"
            mx="auto"
          >
            Works alongside your AI agent in Cursor, VS Code, Claude Code, and more
          </Text>
        </Box>

        {/* Desktop background with wallpaper */}
        <Box
          borderRadius="20px"
          p={{ base: 6, md: 10 }}
          position="relative"
          overflow="hidden"
          bg={isDark ? '#1a1a1a' : '#2d2d2d'}
          boxShadow={isDark
            ? '0 50px 100px -20px rgba(0, 0, 0, 0.7), 0 30px 60px -15px rgba(0, 0, 0, 0.5)'
            : '0 50px 100px -20px rgba(0, 0, 0, 0.25), 0 30px 60px -15px rgba(0, 0, 0, 0.15)'}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: isDark ? 0.4 : 0.5,
            pointerEvents: 'none',
          }}
          _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDark
              ? 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)'
              : 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
            pointerEvents: 'none',
          }}
        >
          {/* Inner demo container */}
          <Box
            bg={containerBg}
            borderRadius="12px"
            border="1px solid"
            borderColor={containerBorder}
            overflow="hidden"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.4)"
            position="relative"
            zIndex={2}
          >
          {/* Browser chrome */}
          <Flex
            bg={chromeBg}
            p={3}
            align="center"
            gap={3}
            borderBottom="1px solid"
            borderColor={containerBorder}
          >
            <Flex gap={2}>
              <Box w="12px" h="12px" borderRadius="full" bg="#ef4444" />
              <Box w="12px" h="12px" borderRadius="full" bg="#eab308" />
              <Box w="12px" h="12px" borderRadius="full" bg="#22c55e" />
            </Flex>
            <Flex gap={1} flex={1}>
              <TabButton active={step < 5} isDark={isDark}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
                Editor
              </TabButton>
              <TabButton active={step >= 5} isDark={isDark}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Browser
              </TabButton>
            </Flex>
          </Flex>

          {/* Content area */}
          <Flex h={{ base: '400px', md: '480px' }} position="relative">
            {/* Editor panel */}
            <Flex flex={1} direction="column" borderRight="1px solid" borderColor={containerBorder}>
              <Flex
                bg={chromeBg}
                px={4}
                py={2}
                align="center"
                gap={2}
                fontSize="12px"
                color={textMuted}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                myrkup-overview.md
              </Flex>
              <Box flex={1} p={4} fontFamily="mono" fontSize="13px" lineHeight={1.6} color={textSecondary} overflow="hidden">
                <EditorLine num={1} isDark={isDark}><span style={{ color: '#10b981', fontWeight: 600 }}># What is myrkup?</span></EditorLine>
                <EditorLine num={2} isDark={isDark}></EditorLine>
                <EditorLine num={3} isDark={isDark}><span style={{ color: textPrimary, fontWeight: 600 }}>**Encrypted markdown sharing**</span> for AI workflows.</EditorLine>
                <EditorLine num={4} isDark={isDark}></EditorLine>
                <EditorLine num={5} isDark={isDark}><span style={{ color: '#10b981' }}>## How it works</span></EditorLine>
                <EditorLine num={6} isDark={isDark}></EditorLine>
                <EditorLine num={7} isDark={isDark}><span style={{ color: '#fbbf24' }}>1.</span> AI generates markdown (Claude, GPT, Cursor)</EditorLine>
                <EditorLine num={8} isDark={isDark}><span style={{ color: '#fbbf24' }}>2.</span> myrkup encrypts with <code style={{ color: '#f472b6', background: codeBg, padding: '2px 6px', borderRadius: '4px' }}>`AES-256-GCM`</code></EditorLine>
                <EditorLine num={9} isDark={isDark}><span style={{ color: '#fbbf24' }}>3.</span> Share a link - no account needed to view</EditorLine>
                <EditorLine num={10} isDark={isDark}></EditorLine>
                <EditorLine num={11} isDark={isDark}><span style={{ color: '#10b981' }}>## Features</span></EditorLine>
                <EditorLine num={12} isDark={isDark}></EditorLine>
                <EditorLine num={13} isDark={isDark}><span style={{ color: '#fbbf24' }}>-</span> End-to-end encryption</EditorLine>
                <EditorLine num={14} isDark={isDark}><span style={{ color: '#fbbf24' }}>-</span> Passcode protection</EditorLine>
                <EditorLine num={15} isDark={isDark}><span style={{ color: '#fbbf24' }}>-</span> Auto-expiration & burn after read</EditorLine>
              </Box>
            </Flex>

            {/* Chat panel */}
            <Flex w={{ base: '280px', md: '320px' }} direction="column" bg={panelBg} display={{ base: 'none', md: 'flex' }}>
              <Flex px={4} py={3} borderBottom="1px solid" borderColor={containerBorder} align="center" gap={2} fontSize="13px" fontWeight={500}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <Text color={textPrimary}>AI Chat</Text>
              </Flex>
              <Flex flex={1} direction="column" gap={4} p={4} overflow="auto">
                {/* User message */}
                <ChatMessage visible={step >= 1} isUser isDark={isDark}>
                  Share this overview with the team
                </ChatMessage>

                {/* Assistant thinking */}
                <ChatMessage visible={step >= 2} isDark={isDark}>
                  <Text fontSize="11px" color="#a855f7" mb={1} fontWeight={500}>AI Assistant</Text>
                  <Text>I'll create an encrypted share for this overview.</Text>
                  <Box bg={containerBg} border="1px solid" borderColor={containerBorder} borderRadius="8px" p={3} mt={2} fontSize="12px">
                    <Text color="#10b981" fontFamily="mono" fontWeight={500}>share_markdown</Text>
                    <Flex align="center" gap={2} mt={1} color={textMuted} fontSize="11px">
                      {step < 3 ? (
                        <>
                          <Box
                            w="12px"
                            h="12px"
                            border="2px solid"
                            borderColor={containerBorder}
                            borderTopColor="#10b981"
                            borderRadius="full"
                            animation="spin 1s linear infinite"
                            sx={{ '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }}
                          />
                          <Text>Creating share...</Text>
                        </>
                      ) : (
                        <>
                          <Text color="#10b981">✓</Text>
                          <Text>Share created</Text>
                        </>
                      )}
                    </Flex>
                  </Box>
                </ChatMessage>

                {/* Link message */}
                <ChatMessage visible={step >= 4} isDark={isDark}>
                  <Text fontSize="11px" color="#a855f7" mb={1} fontWeight={500}>AI Assistant</Text>
                  <Text>Done! Here's your encrypted link:</Text>
                  <Box
                    bg="rgba(16, 185, 129, 0.1)"
                    border="1px solid"
                    borderColor="rgba(16, 185, 129, 0.3)"
                    borderRadius="6px"
                    p={2}
                    mt={2}
                    fontFamily="mono"
                    fontSize="11px"
                    color="#10b981"
                    wordBreak="break-all"
                  >
                    https://myrkup.com/view/k8x2m9p
                  </Box>
                  <Text mt={2} fontSize="12px" color={textMuted}>
                    Passcode: <Text as="span" color="#10b981" fontFamily="mono">team2024</Text>
                  </Text>
                </ChatMessage>
              </Flex>
            </Flex>

            {/* Browser panel (slides in) */}
            <Box
              position="absolute"
              top={0}
              right={0}
              w="100%"
              h="100%"
              bg={panelBg}
              transform={step >= 5 ? 'translateX(0)' : 'translateX(100%)'}
              transition="transform 0.5s ease"
              display="flex"
              flexDirection="column"
              zIndex={10}
            >
              {/* URL bar */}
              <Flex bg={chromeBg} px={4} py={2} align="center" gap={3}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                <Flex
                  flex={1}
                  bg={inputBg}
                  border="1px solid"
                  borderColor={inputBorder}
                  borderRadius="6px"
                  px={3}
                  py={2}
                  fontSize="12px"
                  color="#10b981"
                  fontFamily="mono"
                  align="center"
                  gap={2}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  myrkup.com/view/k8x2m9p
                </Flex>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                </svg>
              </Flex>

              {/* Content */}
              <Flex flex={1} align="center" justify="center" p={8} position="relative">
                {/* Passcode prompt */}
                <Box
                  bg={containerBg}
                  border="1px solid"
                  borderColor={containerBorder}
                  borderRadius="12px"
                  p={8}
                  maxW="360px"
                  w="100%"
                  textAlign="center"
                  opacity={step >= 6 && step < 8 ? 1 : 0}
                  transform={step >= 6 && step < 8 ? 'scale(1)' : 'scale(0.95)'}
                  transition="opacity 0.4s, transform 0.4s"
                  display={step >= 8 ? 'none' : 'block'}
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" style={{ margin: '0 auto 16px' }}>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <Text fontSize="1.25rem" fontWeight={600} mb={2} color={textPrimary}>This share is protected</Text>
                  <Text fontSize="0.875rem" color={textMuted} mb={6}>Enter the passcode to view the content</Text>
                  <Flex gap={2}>
                    <Box
                      flex={1}
                      bg={inputBg}
                      border="1px solid"
                      borderColor={inputBorder}
                      borderRadius="8px"
                      px={4}
                      py={3}
                      fontSize="14px"
                      color={textPrimary}
                      fontFamily="mono"
                      letterSpacing="2px"
                      textAlign="left"
                    >
                      {passcodeText || <Text as="span" color={textMuted} letterSpacing="normal">Enter passcode</Text>}
                    </Box>
                    <Flex
                      bg="#10b981"
                      borderRadius="8px"
                      px={5}
                      align="center"
                      justify="center"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0c0a09" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </Flex>
                  </Flex>
                </Box>

                {/* Rendered content */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  p={6}
                  overflow="auto"
                  opacity={step >= 8 ? 1 : 0}
                  transform={step >= 8 ? 'translateY(0)' : 'translateY(20px)'}
                  transition="opacity 0.5s, transform 0.5s"
                  display={step < 8 ? 'none' : 'block'}
                >
                  <Box maxW="700px">
                    <Text as="h1" fontSize="2rem" fontWeight={700} mb={4} color={textPrimary}>What is myrkup?</Text>
                    <Text color={textSecondary} lineHeight={1.7} mb={4}>
                      <Text as="strong" color={textPrimary}>Encrypted markdown sharing</Text> for AI workflows.
                    </Text>
                    <Text as="h2" fontSize="1.25rem" fontWeight={600} mt={6} mb={3} color={textPrimary}>How it works</Text>
                    <Box as="ul" pl={6} color={textSecondary}>
                      <Text as="li" mb={2}>AI generates markdown (Claude, GPT, Cursor)</Text>
                      <Text as="li" mb={2}>myrkup encrypts with <code style={{ background: codeBg, padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9em', color: '#f472b6' }}>AES-256-GCM</code></Text>
                      <Text as="li" mb={2}>Share a link - no account needed to view</Text>
                    </Box>
                    <Text as="h2" fontSize="1.25rem" fontWeight={600} mt={6} mb={3} color={textPrimary}>Features</Text>
                    <Box as="ul" pl={6} color={textSecondary}>
                      <Text as="li" mb={2}>End-to-end encryption</Text>
                      <Text as="li" mb={2}>Passcode protection</Text>
                      <Text as="li" mb={2}>Auto-expiration & burn after read</Text>
                    </Box>
                  </Box>
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Box>
        </Box>

        {/* Progress dots */}
        <Flex justify="center" gap={2} mt={5}>
          {[1, 2, 3, 4, 5].map((dot) => (
            <Box
              key={dot}
              w="8px"
              h="8px"
              borderRadius="full"
              bg={step >= dot ? '#10b981' : containerBorder}
              transition="background 0.3s"
            />
          ))}
        </Flex>
      </Container>
    </Box>

    {/* Second demo section - AI Chat */}
    <Box py={{ base: 16, md: 24 }} bg={sectionBg2}>
      <Container maxW="1000px" px={4}>
        <Box textAlign="center" mb={{ base: 8, md: 12 }}>
          <Text
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight={700}
            color={textPrimary}
            mb={3}
          >
            Or use directly with AI assistants
          </Text>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color={textSecondary}
            maxW="600px"
            mx="auto"
          >
            Share content from Claude, ChatGPT, or any AI chat interface
          </Text>
        </Box>

        {/* Desktop background with wallpaper for chat demo */}
        <Box
          borderRadius="20px"
          p={{ base: 6, md: 10 }}
          position="relative"
          overflow="hidden"
          bg={isDark ? '#1a1a1a' : '#2d2d2d'}
          boxShadow={isDark
            ? '0 50px 100px -20px rgba(0, 0, 0, 0.7), 0 30px 60px -15px rgba(0, 0, 0, 0.5)'
            : '0 50px 100px -20px rgba(0, 0, 0, 0.25), 0 30px 60px -15px rgba(0, 0, 0, 0.15)'}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1200&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: isDark ? 0.4 : 0.5,
            pointerEvents: 'none',
          }}
          _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDark
              ? 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)'
              : 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
            pointerEvents: 'none',
          }}
        >
          {/* Inner chat container */}
          <Box
            bg={containerBg}
            borderRadius="12px"
            border="1px solid"
            borderColor={containerBorder}
            overflow="hidden"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.4)"
            position="relative"
            zIndex={2}
          >
              {/* Window chrome - switches between Claude and Browser */}
              <Flex
                bg={chromeBg}
                p={3}
                align="center"
                gap={3}
                borderBottom="1px solid"
                borderColor={containerBorder}
              >
                <Flex gap={2}>
                  <Box w="12px" h="12px" borderRadius="full" bg="#ef4444" />
                  <Box w="12px" h="12px" borderRadius="full" bg="#eab308" />
                  <Box w="12px" h="12px" borderRadius="full" bg="#22c55e" />
                </Flex>
                {step2 < 8 ? (
                  /* Claude header */
                  <Flex flex={1} justify="center" align="center" gap={2}>
                    <Box
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      bg="linear-gradient(135deg, #d97706 0%, #f59e0b 100%)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="10px" color="white" fontWeight={700}>C</Text>
                    </Box>
                    <Text fontSize="13px" fontWeight={500} color={textPrimary}>Claude</Text>
                  </Flex>
                ) : (
                  /* Browser title */
                  <Flex flex={1} justify="center" align="center" gap={2}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                    <Text fontSize="13px" fontWeight={500} color={textPrimary}>myrkup.com</Text>
                  </Flex>
                )}
                <Box w="48px" />
              </Flex>

              {/* Content area with browser overlay */}
              <Box h={{ base: '380px', md: '420px' }} position="relative" overflow="hidden">
                {/* Chat content */}
                <Flex direction="column" h="100%" maxW="700px" mx="auto" p={6} gap={5}>
                  {/* User message */}
                  <Box
                    alignSelf="flex-end"
                    bg={isDark ? '#292524' : '#e7e5e4'}
                    px={4}
                    py={3}
                    borderRadius="18px 18px 4px 18px"
                    maxW="85%"
                    fontSize="14px"
                    color={textPrimary}
                    opacity={step2 >= 1 ? 1 : 0}
                    transform={step2 >= 1 ? 'translateY(0)' : 'translateY(10px)'}
                    transition="opacity 0.4s, transform 0.4s"
                  >
                    Can you create a project status report for Q4? Include milestones, blockers, and next steps.
                  </Box>

                  {/* Assistant message with tool call */}
                  <Flex direction="column" gap={3} maxW="90%">
                    <Box
                      fontSize="14px"
                      color={textPrimary}
                      lineHeight={1.7}
                      opacity={step2 >= 2 ? 1 : 0}
                      transform={step2 >= 2 ? 'translateY(0)' : 'translateY(10px)'}
                      transition="opacity 0.4s, transform 0.4s"
                    >
                      I'll create a comprehensive Q4 status report and share it securely with your team.
                    </Box>

                    {/* Tool call card */}
                    <Box
                      bg={panelBg}
                      border="1px solid"
                      borderColor={containerBorder}
                      borderRadius="12px"
                      overflow="hidden"
                      opacity={step2 >= 3 ? 1 : 0}
                      transform={step2 >= 3 ? 'translateY(0)' : 'translateY(10px)'}
                      transition="opacity 0.4s, transform 0.4s"
                    >
                      <Flex
                        bg={chromeBg}
                        px={4}
                        py={2}
                        align="center"
                        gap={2}
                        borderBottom="1px solid"
                        borderColor={containerBorder}
                      >
                        {step2 < 5 ? (
                          <Box
                            w="10px"
                            h="10px"
                            border="2px solid"
                            borderColor={containerBorder}
                            borderTopColor="#10b981"
                            borderRadius="full"
                            animation="spin 1s linear infinite"
                            sx={{ '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }}
                          />
                        ) : (
                          <Box w="8px" h="8px" borderRadius="full" bg="#10b981" />
                        )}
                        <Text fontSize="12px" fontFamily="mono" color="#10b981" fontWeight={500}>
                          share_markdown
                        </Text>
                        <Text fontSize="11px" color={textMuted} ml="auto">
                          {step2 < 5 ? 'running...' : 'completed'}
                        </Text>
                      </Flex>
                      <Box
                        p={4}
                        fontSize="13px"
                        fontFamily="mono"
                        opacity={step2 >= 4 ? 1 : 0}
                        maxH={step2 >= 4 ? '200px' : '0'}
                        transition="opacity 0.3s, max-height 0.3s"
                        overflow="hidden"
                      >
                        <Text color={textMuted} mb={2}>title: "Q4 Project Status Report"</Text>
                        <Text color={textMuted} mb={2}>format: "markdown"</Text>
                        <Text color={textMuted} mb={2}>expires: "7 days"</Text>
                        <Text color={textMuted}>passcode: "status2024"</Text>
                      </Box>
                    </Box>

                    {/* Result */}
                    <Box
                      fontSize="14px"
                      color={textPrimary}
                      lineHeight={1.7}
                      opacity={step2 >= 6 ? 1 : 0}
                      transform={step2 >= 6 ? 'translateY(0)' : 'translateY(10px)'}
                      transition="opacity 0.4s, transform 0.4s"
                    >
                      Done! Here's your encrypted share link:
                    </Box>
                    <Box
                      bg="rgba(16, 185, 129, 0.1)"
                      border="1px solid"
                      borderColor="rgba(16, 185, 129, 0.3)"
                      borderRadius="8px"
                      p={3}
                      fontFamily="mono"
                      fontSize="13px"
                      color="#10b981"
                      opacity={step2 >= 7 ? 1 : 0}
                      transform={step2 >= 7 ? 'translateY(0)' : 'translateY(10px)'}
                      transition="opacity 0.4s, transform 0.4s"
                    >
                      https://myrkup.com/view/q4report#status2024
                    </Box>
                    <Text
                      fontSize="13px"
                      color={textMuted}
                      opacity={step2 >= 7 ? 1 : 0}
                      transform={step2 >= 7 ? 'translateY(0)' : 'translateY(10px)'}
                      transition="opacity 0.4s, transform 0.4s"
                    >
                      Expires in 7 days
                    </Text>
                  </Flex>
                </Flex>

                {/* Browser panel (slides in) */}
                <Box
                  position="absolute"
                  top={0}
                  right={0}
                  w="100%"
                  h="100%"
                  bg={panelBg}
                  transform={step2 >= 8 ? 'translateX(0)' : 'translateX(100%)'}
                  transition="transform 0.5s ease"
                  display="flex"
                  flexDirection="column"
                  zIndex={10}
                >
                  {/* URL bar */}
                  <Flex bg={chromeBg} px={4} py={2} align="center" gap={3}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    <Flex
                      flex={1}
                      bg={inputBg}
                      border="1px solid"
                      borderColor={inputBorder}
                      borderRadius="6px"
                      px={3}
                      py={2}
                      fontSize="12px"
                      color="#10b981"
                      fontFamily="mono"
                      align="center"
                      gap={2}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      myrkup.com/view/q4report#status2024
                    </Flex>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </Flex>

                  {/* Rendered content */}
                  <Box
                    flex={1}
                    p={6}
                    overflow="auto"
                    opacity={step2 >= 9 ? 1 : 0}
                    transform={step2 >= 9 ? 'translateY(0)' : 'translateY(20px)'}
                    transition="opacity 0.5s, transform 0.5s"
                  >
                    <Box maxW="700px" mx="auto">
                      <Text as="h1" fontSize="1.75rem" fontWeight={700} mb={4} color={textPrimary}>Q4 Project Status Report</Text>
                      <Text as="h2" fontSize="1.125rem" fontWeight={600} mt={5} mb={3} color={textPrimary}>Milestones</Text>
                      <Box as="ul" pl={6} color={textSecondary} fontSize="14px">
                        <Text as="li" mb={2}>API v2.0 launched - October 15</Text>
                        <Text as="li" mb={2}>Mobile app beta released - November 1</Text>
                        <Text as="li" mb={2}>Enterprise tier completed - December 10</Text>
                      </Box>
                      <Text as="h2" fontSize="1.125rem" fontWeight={600} mt={5} mb={3} color={textPrimary}>Blockers</Text>
                      <Box as="ul" pl={6} color={textSecondary} fontSize="14px">
                        <Text as="li" mb={2}>Waiting on security audit results</Text>
                        <Text as="li" mb={2}>Third-party integration delays</Text>
                      </Box>
                      <Text as="h2" fontSize="1.125rem" fontWeight={600} mt={5} mb={3} color={textPrimary}>Next Steps</Text>
                      <Box as="ul" pl={6} color={textSecondary} fontSize="14px">
                        <Text as="li" mb={2}>Finalize Q1 roadmap by Dec 20</Text>
                        <Text as="li" mb={2}>Schedule stakeholder review</Text>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Input area - hidden when browser is shown */}
              <Flex
                borderTop="1px solid"
                borderColor={containerBorder}
                p={4}
                gap={3}
                align="center"
                opacity={step2 >= 8 ? 0 : 1}
                maxH={step2 >= 8 ? '0' : '100px'}
                overflow="hidden"
                transition="opacity 0.3s, max-height 0.3s"
              >
                <Flex
                  flex={1}
                  bg={inputBg}
                  border="1px solid"
                  borderColor={inputBorder}
                  borderRadius="24px"
                  px={4}
                  py={3}
                  align="center"
                >
                  <Text fontSize="14px" color={textMuted}>Message Claude...</Text>
                </Flex>
                <Flex
                  w="40px"
                  h="40px"
                  bg={isDark ? '#292524' : '#e7e5e4'}
                  borderRadius="full"
                  align="center"
                  justify="center"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </Flex>
              </Flex>
            </Box>
          </Box>

        {/* Progress dots for demo 2 */}
        <Flex justify="center" gap={2} mt={5}>
          {[1, 3, 5, 7, 9].map((dot, i) => (
            <Box
              key={i}
              w="8px"
              h="8px"
              borderRadius="full"
              bg={step2 >= dot ? '#10b981' : containerBorder}
              transition="background 0.3s"
            />
          ))}
        </Flex>
      </Container>
    </Box>

    {/* Third demo section - Editor Dashboard */}
    <Box py={{ base: 16, md: 24 }} bg={sectionBg1}>
      <Container maxW="1000px" px={4}>
        <Box textAlign="center" mb={{ base: 8, md: 12 }}>
          <Text
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight={700}
            color={textPrimary}
            mb={3}
          >
            All your shares in one place
          </Text>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color={textSecondary}
            maxW="600px"
            mx="auto"
          >
            Access, edit, and manage all your shared markdowns from a single dashboard
          </Text>
        </Box>

        {/* Desktop background with wallpaper for editor demo */}
        <Box
          borderRadius="20px"
          p={{ base: 6, md: 10 }}
          position="relative"
          overflow="hidden"
          bg={isDark ? '#1a1a1a' : '#2d2d2d'}
          boxShadow={isDark
            ? '0 50px 100px -20px rgba(0, 0, 0, 0.7), 0 30px 60px -15px rgba(0, 0, 0, 0.5)'
            : '0 50px 100px -20px rgba(0, 0, 0, 0.25), 0 30px 60px -15px rgba(0, 0, 0, 0.15)'}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1200&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: isDark ? 0.4 : 0.5,
            pointerEvents: 'none',
          }}
          _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDark
              ? 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)'
              : 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
            pointerEvents: 'none',
          }}
        >
          {/* Inner editor container */}
          <Box
            bg={containerBg}
            borderRadius="12px"
            border="1px solid"
            borderColor={containerBorder}
            overflow="hidden"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.4)"
            position="relative"
            zIndex={2}
          >
            {/* Window chrome */}
            <Flex
              bg={chromeBg}
              p={3}
              align="center"
              gap={3}
              borderBottom="1px solid"
              borderColor={containerBorder}
            >
              <Flex gap={2}>
                <Box w="12px" h="12px" borderRadius="full" bg="#ef4444" />
                <Box w="12px" h="12px" borderRadius="full" bg="#eab308" />
                <Box w="12px" h="12px" borderRadius="full" bg="#22c55e" />
              </Flex>
              <Box w="48px" />
            </Flex>

            {/* Content area */}
            <Flex h={{ base: '380px', md: '420px' }}>
              {/* Sidebar mimicking actual editor */}
              <Flex
                w={{ base: '200px', md: '240px' }}
                borderRight="1px solid"
                borderColor={containerBorder}
                bg={panelBg}
                direction="column"
                opacity={step3 >= 1 ? 1 : 0}
                transition="opacity 0.4s"
              >
                {/* Header with logo and new button */}
                <Flex px={3} py={3} align="center" justify="space-between" borderBottom="1px solid" borderColor={containerBorder}>
                  <Text fontSize="14px" fontWeight={700} fontFamily="'Inter', sans-serif" color={textPrimary}>
                    myrkup
                  </Text>
                  <Flex
                    w="28px"
                    h="28px"
                    bg={isDark ? '#292524' : '#e7e5e4'}
                    borderRadius="6px"
                    align="center"
                    justify="center"
                    cursor="pointer"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={textSecondary} strokeWidth="2">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                  </Flex>
                </Flex>

                {/* Drafts section */}
                <Box borderBottom="1px solid" borderColor={containerBorder}>
                  <Flex px={3} py={2} align="center" gap={2}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                    <Text fontSize="11px" fontWeight={600} color={textMuted} textTransform="uppercase" letterSpacing="wider">
                      Drafts
                    </Text>
                    <Text fontSize="10px" color={textMuted} ml="auto">1</Text>
                  </Flex>
                  <Box px={2} pb={2}>
                    <Flex
                      p={2}
                      borderRadius="6px"
                      gap={2}
                      align="center"
                      bg={isDark ? '#1c1917' : '#f5f5f4'}
                      fontSize="12px"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <Text color={textSecondary} noOfLines={1}>untitled.md</Text>
                    </Flex>
                  </Box>
                </Box>

                {/* Published section */}
                <Box flex={1} overflow="auto">
                  <Flex px={3} py={2} align="center" gap={2}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                    <Text fontSize="11px" fontWeight={600} color={textMuted} textTransform="uppercase" letterSpacing="wider">
                      Published
                    </Text>
                    <Text fontSize="10px" color={textMuted} ml="auto">4</Text>
                  </Flex>
                  <Box px={2} pb={2}>
                    {/* Folder: Projects */}
                    <Flex
                      p={2}
                      borderRadius="6px"
                      gap={1.5}
                      align="center"
                      cursor="pointer"
                      mb={0.5}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2.5" style={{ transform: 'rotate(90deg)' }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                      <Text fontSize="12px" color={textPrimary} fontWeight={500}>Projects</Text>
                      <Text fontSize="10px" color={textMuted} ml="auto">2</Text>
                    </Flex>
                    {/* Indented items under Projects folder */}
                    <Box pl={4}>
                      {/* Share item 1 */}
                      <ShareItem
                        title="q4-project-status.md"
                        icon="file"
                        isSelected={step3 >= 2 && step3 < 5}
                        isHovered={step3 === 2}
                        isDark={isDark}
                        hasLock
                      />
                      {/* Share item 4 */}
                      <ShareItem
                        title="product-roadmap.md"
                        icon="file"
                        isSelected={false}
                        isHovered={false}
                        isDark={isDark}
                      />
                    </Box>
                    {/* Share item 2 - root level */}
                    <ShareItem
                      title="api-documentation.md"
                      icon="code"
                      isSelected={step3 >= 5}
                      isHovered={step3 === 5}
                      isDark={isDark}
                    />
                    {/* Share item 3 - root level */}
                    <ShareItem
                      title="team-meeting-notes.md"
                      icon="file"
                      isSelected={false}
                      isHovered={false}
                      isDark={isDark}
                      hasFlame
                    />
                  </Box>
                </Box>

                {/* Footer with user */}
                <Flex
                  px={3}
                  py={2}
                  borderTop="1px solid"
                  borderColor={containerBorder}
                  align="center"
                  gap={2}
                >
                  <Box
                    w="24px"
                    h="24px"
                    borderRadius="full"
                    bg="#805AD5"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="10px" color="white" fontWeight={600}>JD</Text>
                  </Box>
                  <Text fontSize="12px" color={textSecondary} noOfLines={1}>john_doe</Text>
                  <Box ml="auto">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                  </Box>
                </Flex>
              </Flex>

              {/* Main content area */}
              <Box flex={1} position="relative" overflow="hidden" bg={containerBg}>
                {/* Empty state */}
                <Flex
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  align="center"
                  justify="center"
                  direction="column"
                  gap={3}
                  opacity={step3 < 3 ? 1 : 0}
                  transition="opacity 0.3s"
                  pointerEvents={step3 < 3 ? 'auto' : 'none'}
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <Text color={textMuted} fontSize="14px">Select a share to preview</Text>
                </Flex>

                {/* Content preview 1 - Q4 Status */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  overflow="hidden"
                  opacity={step3 >= 4 && step3 < 7 ? 1 : 0}
                  transform={step3 >= 4 && step3 < 7 ? 'translateY(0)' : 'translateY(20px)'}
                  transition="opacity 0.4s, transform 0.4s"
                  pointerEvents={step3 >= 4 && step3 < 7 ? 'auto' : 'none'}
                >
                  {/* Toolbar */}
                  <Flex px={4} py={2} borderBottom="1px solid" borderColor={containerBorder} align="center" gap={3}>
                    <Text fontSize="13px" fontWeight={500} color={textPrimary}>Q4 Project Status</Text>
                    <Flex gap={2} ml="auto">
                      <Box px={2} py={1} bg="rgba(16, 185, 129, 0.1)" borderRadius="4px">
                        <Text fontSize="10px" color="#10b981" fontWeight={500}>Active</Text>
                      </Box>
                      <Box px={2} py={1} bg={chromeBg} borderRadius="4px" display="flex" alignItems="center" gap={1}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <Text fontSize="10px" color={textMuted}>5 days</Text>
                      </Box>
                    </Flex>
                    <Flex
                      px={3}
                      py={1.5}
                      bg="#10b981"
                      borderRadius="6px"
                      align="center"
                      gap={1.5}
                      cursor="pointer"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0c0a09" strokeWidth="2">
                        <circle cx="18" cy="5" r="3"/>
                        <circle cx="6" cy="12" r="3"/>
                        <circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      <Text fontSize="11px" fontWeight={500} color="#0c0a09">Share</Text>
                    </Flex>
                  </Flex>
                  {/* Content */}
                  <Box p={6} overflow="auto" h="calc(100% - 45px)">
                    <Text as="h1" fontSize="1.5rem" fontWeight={700} mb={3} color={textPrimary}>Q4 Project Status</Text>
                    <Text as="h2" fontSize="1rem" fontWeight={600} mt={4} mb={2} color={textPrimary}>Milestones</Text>
                    <Box as="ul" pl={5} color={textSecondary} fontSize="13px">
                      <Text as="li" mb={1}>API v2.0 launched</Text>
                      <Text as="li" mb={1}>Mobile app beta released</Text>
                      <Text as="li" mb={1}>Enterprise tier completed</Text>
                    </Box>
                    <Text as="h2" fontSize="1rem" fontWeight={600} mt={4} mb={2} color={textPrimary}>Next Steps</Text>
                    <Box as="ul" pl={5} color={textSecondary} fontSize="13px">
                      <Text as="li" mb={1}>Finalize Q1 roadmap</Text>
                      <Text as="li" mb={1}>Schedule stakeholder review</Text>
                    </Box>
                  </Box>
                </Box>

                {/* Content preview 2 - API Docs */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  overflow="hidden"
                  opacity={step3 >= 7 ? 1 : 0}
                  transform={step3 >= 7 ? 'translateY(0)' : 'translateY(20px)'}
                  transition="opacity 0.4s, transform 0.4s"
                  pointerEvents={step3 >= 7 ? 'auto' : 'none'}
                >
                  {/* Toolbar */}
                  <Flex px={4} py={2} borderBottom="1px solid" borderColor={containerBorder} align="center" gap={3}>
                    <Text fontSize="13px" fontWeight={500} color={textPrimary}>API Documentation</Text>
                    <Flex gap={2} ml="auto">
                      <Box px={2} py={1} bg="rgba(16, 185, 129, 0.1)" borderRadius="4px">
                        <Text fontSize="10px" color="#10b981" fontWeight={500}>Active</Text>
                      </Box>
                      <Box px={2} py={1} bg={chromeBg} borderRadius="4px">
                        <Text fontSize="10px" color={textMuted}>No expiration</Text>
                      </Box>
                    </Flex>
                    <Flex
                      px={3}
                      py={1.5}
                      bg="#10b981"
                      borderRadius="6px"
                      align="center"
                      gap={1.5}
                      cursor="pointer"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0c0a09" strokeWidth="2">
                        <circle cx="18" cy="5" r="3"/>
                        <circle cx="6" cy="12" r="3"/>
                        <circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      <Text fontSize="11px" fontWeight={500} color="#0c0a09">Share</Text>
                    </Flex>
                  </Flex>
                  {/* Content */}
                  <Box p={6} overflow="auto" h="calc(100% - 45px)">
                    <Text as="h1" fontSize="1.5rem" fontWeight={700} mb={3} color={textPrimary}>API Documentation</Text>
                    <Text as="h2" fontSize="1rem" fontWeight={600} mt={4} mb={2} color={textPrimary}>Authentication</Text>
                    <Text color={textSecondary} fontSize="13px" mb={3}>
                      All API requests require a Bearer token in the Authorization header.
                    </Text>
                    <Box bg={codeBg} p={3} borderRadius="6px" fontFamily="mono" fontSize="12px" color={textSecondary}>
                      <Text>Authorization: Bearer {'<your-api-key>'}</Text>
                    </Box>
                    <Text as="h2" fontSize="1rem" fontWeight={600} mt={4} mb={2} color={textPrimary}>Endpoints</Text>
                    <Box as="ul" pl={5} color={textSecondary} fontSize="13px">
                      <Text as="li" mb={1}><code style={{ color: '#10b981' }}>POST /shares</code> - Create a new share</Text>
                      <Text as="li" mb={1}><code style={{ color: '#10b981' }}>GET /shares/:id</code> - Get share details</Text>
                      <Text as="li" mb={1}><code style={{ color: '#10b981' }}>DELETE /shares/:id</code> - Delete a share</Text>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Flex>
          </Box>
        </Box>

        {/* Progress dots for demo 3 */}
        <Flex justify="center" gap={2} mt={5}>
          {[1, 3, 5, 7].map((dot, i) => (
            <Box
              key={i}
              w="8px"
              h="8px"
              borderRadius="full"
              bg={step3 >= dot ? '#10b981' : containerBorder}
              transition="background 0.3s"
            />
          ))}
        </Flex>
      </Container>
    </Box>
    </>
  )
}

function TabButton({ active, isDark, children }: { active: boolean; isDark: boolean; children: React.ReactNode }) {
  return (
    <Flex
      bg={active ? (isDark ? '#0c0a09' : '#ffffff') : (isDark ? '#1c1917' : '#e7e5e4')}
      px={4}
      py={1.5}
      borderRadius="6px 6px 0 0"
      fontSize="12px"
      color={isDark ? '#a8a29e' : '#78716c'}
      align="center"
      gap={2}
      opacity={active ? 1 : 0.6}
      transition="opacity 0.3s, background 0.3s"
    >
      {children}
    </Flex>
  )
}

function EditorLine({ num, isDark, children }: { num: number; isDark: boolean; children?: React.ReactNode }) {
  return (
    <Flex h="21px" align="center">
      <Text w="24px" minW="24px" textAlign="right" mr={3} color={isDark ? '#57534e' : '#a8a29e'} userSelect="none" fontSize="13px">{num}</Text>
      <Box flex={1}>{children}</Box>
    </Flex>
  )
}

function ChatMessage({ visible, isUser, isDark, children }: { visible: boolean; isUser?: boolean; isDark: boolean; children: React.ReactNode }) {
  return (
    <Box
      alignSelf={isUser ? 'flex-end' : 'flex-start'}
      bg={isUser ? (isDark ? '#292524' : '#e7e5e4') : 'transparent'}
      px={isUser ? 3.5 : 0}
      py={isUser ? 2.5 : 0}
      borderRadius={isUser ? '12px 12px 4px 12px' : '0'}
      fontSize="13px"
      maxW={isUser ? '90%' : '95%'}
      opacity={visible ? 1 : 0}
      transform={visible ? 'translateY(0)' : 'translateY(10px)'}
      transition="opacity 0.4s, transform 0.4s"
      color={isDark ? '#fafaf9' : '#1c1917'}
    >
      {children}
    </Box>
  )
}

function ShareItem({ title, icon, isSelected, isHovered, isDark, hasLock, hasFlame }: {
  title: string
  icon: 'file' | 'code' | 'users' | 'map'
  isSelected: boolean
  isHovered: boolean
  isDark: boolean
  hasLock?: boolean
  hasFlame?: boolean
}) {
  const textPrimary = isDark ? '#fafaf9' : '#1c1917'
  const textMuted = isDark ? '#78716c' : '#a8a29e'
  const hoverBg = isDark ? '#292524' : '#f5f5f4'
  const selectedBg = isDark ? '#1c1917' : '#ffffff'

  const icons = {
    file: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
    code: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
    users: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    map: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
  }

  return (
    <Flex
      p={2}
      borderRadius="6px"
      gap={2}
      align="center"
      bg={isSelected ? selectedBg : (isHovered ? hoverBg : 'transparent')}
      border="1px solid"
      borderColor={isSelected ? (isDark ? '#292524' : '#e7e5e4') : 'transparent'}
      transition="all 0.2s"
      cursor="pointer"
      mb={0.5}
    >
      <Box color={isSelected ? '#10b981' : textMuted}>
        {icons[icon]}
      </Box>
      <Box flex={1} minW={0}>
        <Flex align="center" gap={1}>
          <Text
            fontSize="12px"
            fontWeight={isSelected ? 500 : 400}
            color={textPrimary}
            noOfLines={1}
          >
            {title}
          </Text>
          {hasLock && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          )}
          {hasFlame && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2">
              <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
            </svg>
          )}
        </Flex>
      </Box>
    </Flex>
  )
}
